import { ALL_SDK_NETWORKS } from '@/config/query/queryParams'
import { getDefiSdk } from '@/utils/defiSdk'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { MINUTE_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import type { Address } from 'viem'

export const walletBalancesQueryKey = ['walletBalances'] as const

export interface WalletBalancesQueryProps {
  address: Address | undefined
}

function walletBalancesQueryOptions({ address }: WalletBalancesQueryProps) {
  return queryOptions({
    queryKey: [...walletBalancesQueryKey, { account: address }] as const,
    async queryFn() {
      if (!address) return null
      const defiSdk = getDefiSdk()

      const response = await defiSdk.getIdleAssets({
        path: { userAddress: address },
        query: {
          minUsdAssetValueThreshold: 0,
          allowedNetworks: ALL_SDK_NETWORKS.filter((network) => network !== 'swellchain'),
        },
      })

      return response
    },
    select(data) {
      if (data == null) {
        return null
      }

      if (!Array.isArray(data.data)) {
        return null
      }

      const balances = data.data.map((item) => ({
        ...item,
        address: item.address as Address,
        balanceUsd: Number(item.balanceUsd),
        balanceNative: BigInt(item.balanceNative ?? 0),
      }))

      return balances
    },
    enabled: !!address,
    retry: 2,
    staleTime: MINUTE_IN_MILLISECONDS,
    refetchOnWindowFocus: true,
    placeholderData: undefined,
  })
}

export const useWalletBalancesQuery = ({ address }: WalletBalancesQueryProps) => {
  const { data, isFetching } = useQuery({
    ...walletBalancesQueryOptions({ address }),
  })

  return { data, isFetching }
}

export type WalletBalances = ReturnType<typeof useWalletBalancesQuery>['data']
